import { ApiError, api } from "@/shared/api/client";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Field,
  StateMatrix,
  cn,
} from "@/shared/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { z } from "zod";

const ROLES = ["user", "editor", "admin"] as const;
type Role = (typeof ROLES)[number];

const UserSummary = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string().optional(),
  role: z.enum(["admin", "editor", "user"]),
  disabledAt: z.string().optional(),
  createdAt: z.string(),
});
type UserSummary = z.infer<typeof UserSummary>;

const UsersResponse = z.object({
  data: z.array(UserSummary),
  pagination: z.object({ total: z.number() }).partial(),
});

const AclEntry = z.object({
  userId: z.string(),
  catalogId: z.string(),
  catalogName: z.string(),
  perm: z.enum(["read", "write"]),
  grantedAt: z.string(),
});

const CatalogSummary = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
});

const CatalogList = z.object({ data: z.array(CatalogSummary.passthrough()) });

export function AdminPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<UserSummary | null>(null);

  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const raw = await api<unknown>("/v1/users?limit=500");
      return UsersResponse.parse(raw).data;
    },
  });

  return (
    <div className="grid h-full grid-cols-[24rem_1fr]">
      <aside className="flex min-h-0 flex-col overflow-hidden border-r border-[var(--border)] bg-[var(--surface)]">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <h1 className="text-sm font-semibold">Users</h1>
          <CreateUserDialog
            onCreated={() => qc.invalidateQueries({ queryKey: ["admin", "users"] })}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <StateMatrix
            status={users.status}
            data={users.data}
            error={users.error}
            onRetry={() => users.refetch()}
            isEmpty={(d) => d.length === 0}
          >
            {(list) => (
              <ul className="flex flex-col">
                {list.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(u)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm",
                        selected?.id === u.id
                          ? "bg-[var(--surface-2)] font-medium"
                          : "hover:bg-[var(--surface-2)]",
                      )}
                    >
                      <span className="truncate">
                        {u.username}
                        {u.disabledAt && (
                          <span className="ml-2 text-[10px] uppercase text-[var(--danger)]">
                            disabled
                          </span>
                        )}
                      </span>
                      <span className="rounded bg-[var(--surface)] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[var(--fg-muted)]">
                        {u.role}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </StateMatrix>
        </div>
      </aside>
      <section className="min-w-0 overflow-auto">
        {selected ? (
          <UserDetail
            user={selected}
            onChanged={(next) => {
              setSelected(next);
              qc.invalidateQueries({ queryKey: ["admin", "users"] });
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-[var(--fg-muted)]">Select a user to manage.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function CreateUserDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("user");

  const mut = useMutation({
    mutationFn: async () => {
      await api<unknown>("/v1/users", { method: "POST", body: { username, email, role } });
    },
    onSuccess: () => {
      setOpen(false);
      setUsername("");
      setEmail("");
      setRole("user");
      onCreated();
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">New user</Button>
      </DialogTrigger>
      <DialogContent title="Create user">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            mut.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <Field
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.currentTarget.value)}
            required
          />
          <Field
            label="Email"
            type="email"
            hint="The email authentik reports for this person — their first sign-in matches on it."
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
          />
          <label className="flex flex-col gap-1 text-xs font-medium text-[var(--fg-muted)]">
            Role
            <select
              value={role}
              onChange={(e) => setRole(e.currentTarget.value as Role)}
              className="h-9 rounded-md border border-[var(--border)] bg-[var(--surface)] px-2 text-sm text-[var(--fg)]"
            >
              <option value="user">user — read (granted catalogs)</option>
              <option value="editor">editor — write all documentation</option>
              <option value="admin">admin — editor + users</option>
            </select>
          </label>
          {mut.error instanceof ApiError && (
            <p className="text-xs text-[var(--danger)]">{mut.error.message}</p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Creating…" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserDetail({
  user,
  onChanged,
}: {
  user: UserSummary;
  onChanged: (next: UserSummary) => void;
}) {
  const qc = useQueryClient();

  const catalogs = useQuery({
    queryKey: ["admin", "catalogs-all"],
    queryFn: async () => {
      const raw = await api<unknown>("/v1/catalogs?limit=500");
      return CatalogList.parse(raw).data;
    },
  });

  const acl = useQuery({
    queryKey: ["admin", "acl", user.id],
    queryFn: async () => {
      const raw = await api<unknown>(`/v1/users/${encodeURIComponent(user.id)}/acl`);
      return z.array(AclEntry).parse(raw);
    },
  });

  const role = useMutation({
    mutationFn: async (next: Role) => {
      const raw = await api<unknown>(`/v1/users/${encodeURIComponent(user.id)}/role`, {
        method: "PUT",
        body: { role: next },
      });
      return UserSummary.parse(raw);
    },
    onSuccess: (u) => onChanged(u),
  });

  const disable = useMutation({
    mutationFn: async (disabled: boolean) => {
      const raw = await api<unknown>(`/v1/users/${encodeURIComponent(user.id)}/disable`, {
        method: "POST",
        body: { disabled },
      });
      return UserSummary.parse(raw);
    },
    onSuccess: (u) => onChanged(u),
  });

  const setEmail = useMutation({
    mutationFn: async (email: string | null) => {
      const raw = await api<unknown>(`/v1/users/${encodeURIComponent(user.id)}/email`, {
        method: "PUT",
        body: { email },
      });
      return UserSummary.parse(raw);
    },
    onSuccess: (u) => onChanged(u),
  });

  const grant = useMutation({
    mutationFn: async (input: { catalogId: string; perm: "read" | "write" }) => {
      await api<unknown>(`/v1/users/${encodeURIComponent(user.id)}/acl`, {
        method: "PUT",
        body: input,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "acl", user.id] }),
  });

  const revokeAcl = useMutation({
    mutationFn: async (catalogId: string) => {
      await api<null>(
        `/v1/users/${encodeURIComponent(user.id)}/acl/${encodeURIComponent(catalogId)}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "acl", user.id] }),
  });

  const permByCatalog = new Map<string, "read" | "write">();
  for (const r of acl.data ?? []) permByCatalog.set(r.catalogId, r.perm);

  return (
    <div className="flex flex-col gap-8 p-8">
      <header>
        <h2 className="text-2xl font-semibold">{user.username}</h2>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          {user.email ?? "no email"} · {user.role} · created{" "}
          {new Date(user.createdAt).toLocaleDateString()}
        </p>
      </header>

      <section className="flex flex-wrap gap-2">
        {ROLES.filter((r) => r !== user.role).map((r) => (
          <Button
            key={r}
            variant="secondary"
            size="sm"
            onClick={() => role.mutate(r)}
            disabled={role.isPending}
          >
            Make {r}
          </Button>
        ))}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => disable.mutate(!user.disabledAt)}
          disabled={disable.isPending}
        >
          {user.disabledAt ? "Enable" : "Disable"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const next = prompt(
              `Email for ${user.username} (sign-in matches on it)`,
              user.email ?? "",
            );
            if (next !== null) setEmail.mutate(next.trim() || null);
          }}
          disabled={setEmail.isPending}
        >
          Change email
        </Button>
      </section>
      {setEmail.error instanceof ApiError && (
        <p className="text-xs text-[var(--danger)]">{setEmail.error.message}</p>
      )}

      <section>
        <h3 className="text-sm font-semibold">Catalog access</h3>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          Admins bypass ACL and see everything. Set read/write per catalog for regular users.
        </p>
        <StateMatrix
          status={catalogs.status}
          data={catalogs.data}
          error={catalogs.error}
          onRetry={() => catalogs.refetch()}
        >
          {(allCatalogs) => (
            <table className="mt-4 w-full table-fixed border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-left text-[10px] uppercase tracking-wider text-[var(--fg-muted)]">
                  <th className="py-2 pr-4">Catalog</th>
                  <th className="w-24 py-2">None</th>
                  <th className="w-24 py-2">Read</th>
                  <th className="w-24 py-2">Write</th>
                </tr>
              </thead>
              <tbody>
                {allCatalogs.map((c) => {
                  const current = permByCatalog.get(c.id);
                  const cell = (perm: "none" | "read" | "write") => (
                    <td className="py-2">
                      <input
                        type="radio"
                        name={`perm-${c.id}`}
                        checked={(current ?? "none") === perm}
                        disabled={grant.isPending || revokeAcl.isPending}
                        onChange={() => {
                          if (perm === "none") revokeAcl.mutate(c.id);
                          else grant.mutate({ catalogId: c.id, perm });
                        }}
                        className="size-4 accent-[var(--accent)]"
                        aria-label={`${perm} ${c.name}`}
                      />
                    </td>
                  );
                  return (
                    <tr key={c.id} className="border-b border-[var(--border)]/50">
                      <td className="py-2 pr-4">
                        <p className="font-medium">{c.label}</p>
                        <p className="text-[11px] text-[var(--fg-muted)]">{c.name}</p>
                      </td>
                      {cell("none")}
                      {cell("read")}
                      {cell("write")}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </StateMatrix>
      </section>
    </div>
  );
}
